/*
  Solar Outlet product page: buy box behaviour (Concept A, 2026-09-15)

  Five jobs, nothing else:
    1. "Add a pallet": set the quantity input to the pallet count and submit
       through the theme's own Add to Cart button, so Empire's AJAX cart
       handles it exactly like a manual add.
    2. Delivery estimate: POST sku + quantity + suburb + postcode to PIM's
       carrier-service endpoint and render the returned rates.
    3. Repair the added-to-cart flyout image, which Empire renders as
       src="null" for webp product images.
    4. "Buy from": clicking a warehouse row sets the "Ship from" line
       property that PIM's carrier service quotes from at checkout.
    5. Mirror the cart's single warehouse into the cart attribute "Ship from"
       after every add to cart, for the Checkout Blocks pickup rule.

  No jQuery. No dependency on empire.js internals. Safe to load on every
  page: it does nothing unless [data-sj-buybox] is present.
*/
(function () {
  'use strict';

  var STORAGE_KEY = 'sj.estimate.destination';
  // The warehouse is a CART-level choice, not a per-product one. Remembering
  // it and pre-selecting it on every later product keeps a cart unanimous,
  // which is what lets PIM quote freight from that warehouse and despatch the
  // order from it. A cart naming two warehouses is two consignments, and it
  // is quoted and shipped from neither of them.
  var WAREHOUSE_KEY = 'sj.shipfrom.warehouse';

  // Kept for a week, not forever: a choice made for one order should not
  // still be overriding the nearest warehouse months later.
  var WAREHOUSE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

  function readWarehouse() {
    try {
      var raw = window.localStorage.getItem(WAREHOUSE_KEY);
      if (!raw) return '';
      var parsed = raw.charAt(0) === '{' ? JSON.parse(raw) : { name: raw, ts: Date.now() };
      if (!parsed || !parsed.name) return '';
      // A value written before this shape existed has no timestamp of its own;
      // stamp it now so it expires like any other rather than living forever.
      if (raw.charAt(0) !== '{') {
        window.localStorage.setItem(WAREHOUSE_KEY, JSON.stringify(parsed));
      }
      if (parsed.ts && (Date.now() - parsed.ts) > WAREHOUSE_TTL_MS) {
        window.localStorage.removeItem(WAREHOUSE_KEY);
        return '';
      }
      return parsed.name;
    } catch (e) { return ''; }
  }

  function storeWarehouse(name) {
    try {
      if (name) window.localStorage.setItem(WAREHOUSE_KEY, JSON.stringify({ name: name, ts: Date.now() }));
    } catch (e) { /* private mode */ }
  }

  function esc(text) {
    var d = document.createElement('div');
    d.textContent = String(text == null ? '' : text);
    return d.innerHTML;
  }

  function money(cents) {
    var n = Number(cents) / 100;
    if (isNaN(n)) return '';
    return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' AUD';
  }

  function readStored() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function store(dest) {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(dest)); } catch (e) { /* private mode */ }
  }

  // "Est. delivery Thu 17 - Fri 18 Sep" from the dates PIM sends. Same window
  // Shopify shows at checkout, so the two never disagree.
  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // PIM sends the carrier's own ETA plus the transit days, in the rate
  // description: "... Est. delivery Wed 16 Sep (2 business days). Usually
  // despatched within 24 hours of payment". Read the date from the structured
  // field and the transit count from the description, so the page and checkout
  // never disagree.
  function etaInfo(rate) {
    if (!rate.min_delivery_date) return null;
    var d = new Date(rate.min_delivery_date + 'T00:00:00');
    if (isNaN(d.getTime())) return null;

    // Formatted by hand, not toLocaleDateString: the browser renders "Wed,
    // 16 Sept" while PIM says "Wed 16 Sep", and the two must read identically.
    var label = 'Est. delivery ' + DAYS[d.getDay()] + ' ' + d.getDate() + ' ' + MONTHS[d.getMonth()];
    var days = /\((\d+) business day/.exec(rate.description || '');
    var transit = days ? days[1] + ' business day' + (days[1] === '1' ? '' : 's') : '';
    var tip = 'Usually despatched within 24 hours of payment.' +
              (transit ? ' Delivered in ' + transit + ' from your order.' : '');
    return { label: label, tip: tip };
  }

  function qtyInput(box) {
    return box.querySelector('[data-quantity-input]');
  }

  function currentQty(box) {
    var el = qtyInput(box);
    var n = el ? parseInt(el.value, 10) : 1;
    return (isNaN(n) || n < 1) ? 1 : n;
  }

  /* ---------- 1. Add a pallet ---------- */
  function initPallet(box) {
    var btn = box.querySelector('[data-sj-pallet-add]');
    var input = qtyInput(box);
    var atc = document.getElementById('AddToCart');
    if (!btn || !input || !atc) return;

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (btn.disabled || atc.disabled) return;
      var n = parseInt(btn.getAttribute('data-sj-pallet-qty'), 10);
      if (!n || n < 1) return;
      input.value = String(n);
      // Empire watches these to keep its own state in sync.
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      atc.click();
    });
  }

  // A delivery credit (PIM's Shipping::FreightPromo) arrives as the first
  // sentence of the rate's description, always in one of two shapes:
  //   "<label>: $250.00 delivery credit applied, freight was $348.20. "
  //   "<label>: $106.24 delivery credit covers your freight. "
  // Keep this regex in step with FreightPromo.description_for.
  function creditInfo(rate) {
    var m = /^([^:]+): \$([\d,]+\.\d{2}) delivery credit (applied, freight was \$([\d,]+\.\d{2})|covers your freight)/.exec(rate.description || '');
    if (!m) return null;
    return { label: m[1], credit: m[2], was: m[4] || null, covers: !m[4] };
  }

  /* ---------- 2. Delivery estimate ---------- */
  function initEstimate(box) {
    var wrap = box.querySelector('[data-sj-estimate]');
    if (!wrap) return;

    var endpoint = wrap.getAttribute('data-endpoint');
    var suburbEl = wrap.querySelector('[data-sj-suburb]');
    var postcodeEl = wrap.querySelector('[data-sj-postcode]');
    var submit = wrap.querySelector('[data-sj-estimate-submit]');
    var result = wrap.querySelector('[data-sj-estimate-result]');
    var message = wrap.querySelector('[data-sj-estimate-message]');
    var list = wrap.querySelector('[data-sj-estimate-rates]');
    var sku = box.getAttribute('data-sku');
    if (!endpoint || !suburbEl || !postcodeEl || !submit || !sku) return;

    // Pre-fill: last used destination wins, then the customer's default address.
    var stored = readStored();
    suburbEl.value = (stored && stored.suburb) || wrap.getAttribute('data-default-suburb') || '';
    postcodeEl.value = (stored && stored.postcode) || wrap.getAttribute('data-default-postcode') || '';

    function show(text) {
      result.hidden = false;
      message.textContent = text;
      list.innerHTML = '';
    }

    function run() {
      var suburb = suburbEl.value.trim();
      var postcode = postcodeEl.value.trim();
      if (!/^\d{4}$/.test(postcode) || !suburb) {
        show('Enter a suburb and a 4-digit postcode.');
        return;
      }
      store({ suburb: suburb, postcode: postcode });
      show('Calculating…');
      submit.disabled = true;

      // Form-encoded on purpose. PIM has no OPTIONS route on this endpoint,
      // so a JSON body would trigger a CORS preflight that 404s. A
      // form-encoded POST is a "simple request": no preflight, and Rails
      // parses rate[items][][sku] / rate[destination][postal_code] into the
      // same nested params the carrier service already receives.
      var body = new URLSearchParams();
      body.append('rate[items][][sku]', sku);
      body.append('rate[items][][quantity]', String(currentQty(box)));
      body.append('rate[destination][city]', suburb);
      body.append('rate[destination][postal_code]', postcode);
      body.append('rate[destination][country]', 'AU');
      // Quote from the chosen warehouse, the same way checkout will.
      var from = shipFrom(box);
      if (from) body.append('rate[items][][properties][Ship from]', from);

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: body
      })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (data) {
          var rates = (data && data.rates) || [];
          if (!rates.length) {
            show('No automatic rate for ' + suburb + ' ' + postcode + '. We will quote freight manually at checkout.');
            return;
          }
          show('Delivery to ' + suburb + ' ' + postcode + ' for ' + currentQty(box) + ' unit' + (currentQty(box) === 1 ? '' : 's') + ':');
          rates.forEach(function (rate) {
            var li = document.createElement('li');
            var left = document.createElement('span');
            var name = document.createElement('span');
            var price = document.createElement('strong');
            name.textContent = rate.service_name || 'Freight';
            left.appendChild(name);
            var info = etaInfo(rate);
            if (info) {
              var eta = document.createElement('span');
              eta.className = 'sj-estimate__eta';
              eta.textContent = info.label;
              // Tooltip on hover and on focus, and readable to a screen reader.
              var tip = document.createElement('span');
              tip.className = 'sj-tip';
              tip.setAttribute('tabindex', '0');
              tip.setAttribute('role', 'note');
              tip.setAttribute('aria-label', info.tip);
              tip.setAttribute('data-sj-tip', info.tip);
              tip.textContent = 'i';
              eta.appendChild(tip);
              left.appendChild(eta);
            }
            var credit = creditInfo(rate);
            if (credit) {
              var line = document.createElement('span');
              line.className = 'sj-estimate__credit';
              line.textContent = credit.label + ': $' + credit.credit + ' delivery credit' + (credit.was ? ' (freight $' + credit.was + ')' : '');
              left.appendChild(line);
            }
            // PIM returns cents. 0 means "needs a manual quote", never free,
            // unless a delivery credit covered the whole rate.
            price.textContent = credit && credit.covers ? 'Free' : (Number(rate.total_price) > 0 ? money(rate.total_price) : 'Quoted at checkout');
            li.appendChild(left); li.appendChild(price);
            list.appendChild(li);
          });
        })
        .catch(function (err) {
          if (window.console) console.error('Delivery estimate failed', err);
          show('Could not get an estimate right now. Freight is confirmed at checkout.');
        })
        .then(function () { submit.disabled = false; });
    }

    submit.addEventListener('click', function (e) { e.preventDefault(); run(); });
    postcodeEl.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); run(); } });
    suburbEl.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); run(); } });

    // A visible estimate re-runs when the warehouse changes, so the page and
    // checkout never disagree.
    return function rerunIfShown() { if (!result.hidden) run(); };
  }

  /* ---------- 3. Repair the added-to-cart flyout image ---------- */
  // Empire's flyout resizes response.image with a regex that only knows
  // jpg/jpeg/gif/png/bmp/tiff. A webp product image (42 active products)
  // falls through to null and the banner renders <img src="null">. When
  // that happens, swap in the product image the buy box carries.
  function initFlyoutImageFix(box) {
    var fallback = box.getAttribute('data-image');
    if (!fallback || !('MutationObserver' in window)) return;

    function repair(root) {
      var imgs = (root.querySelectorAll ? root.querySelectorAll('[data-atc-banner-product-image] img') : []);
      for (var i = 0; i < imgs.length; i++) {
        var src = imgs[i].getAttribute('src');
        if (!src || src === 'null' || src === 'undefined') imgs[i].setAttribute('src', fallback);
      }
    }

    var observer = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var a = 0; a < added.length; a++) {
          if (added[a].nodeType === 1) repair(added[a]);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- 4. "Buy from" warehouse selection ---------- */
  // Each selectable row carries a real radio named properties[Ship from];
  // the checked radio is what the form submits. Shopify forwards line
  // properties in the carrier-service request, so PIM quotes freight from
  // the chosen warehouse at checkout.
  function shipFrom(box) {
    var checked = box.querySelector('[data-sj-ship-from-radio]:checked');
    return checked ? checked.value : '';
  }

  function initBuyFrom(box, onChange) {
    var table = box.querySelector('[data-sj-buyfrom]');
    if (!table) return;
    var label = box.querySelector('[data-sj-ship-from-label]');
    var note = box.querySelector('[data-sj-buyfrom-elsewhere]');
    var rows = table.querySelectorAll('.sj-buyfrom__row.is-selectable');
    var radios = table.querySelectorAll('[data-sj-ship-from-radio]');

    // Carry the cart's warehouse over to this product when it can supply it.
    // When it cannot, the page keeps its own nearest-in-stock default and says
    // so, because silently shipping this line from somewhere else is exactly
    // the split we are trying to avoid.
    function applyRemembered() {
      var remembered = readWarehouse();
      if (!remembered) return;
      // Only a SELECTABLE nearest row can be switched to: the home row is
      // marked even when it is out of stock, and offering it would render a
      // button that quietly does nothing.
      var nearest = table.querySelector('.sj-buyfrom__row.is-home.is-selectable');
      var nearestName = nearest ? nearest.getAttribute('data-sj-warehouse') : '';

      for (var i = 0; i < radios.length; i++) {
        if (radios[i].value === remembered) {
          if (!radios[i].checked) radios[i].checked = true;
          // Carrying a choice over is only helpful if the customer can see it
          // happened, especially when it is not their nearest warehouse.
          if (note && nearestName && nearestName !== remembered) {
            note.innerHTML = 'Keeping the rest of your cart together by shipping from ' +
              '<strong>' + esc(remembered) + '</strong>. ' +
              '<button type="button" class="sj-buyfrom__switch" data-sj-use-nearest>' +
              'Use ' + esc(nearestName) + ' instead</button>';
            note.hidden = false;
            var swap = note.querySelector('[data-sj-use-nearest]');
            if (swap) {
              swap.addEventListener('click', function () {
                for (var n = 0; n < radios.length; n++) {
                  if (radios[n].value === nearestName) {
                    radios[n].checked = true;
                    storeWarehouse(nearestName);
                    note.hidden = true;
                    sync();
                    return;
                  }
                }
              });
            }
          }
          return;
        }
      }

      if (note) {
        note.textContent = remembered + ' cannot supply this item, so it would ship separately from ' +
          shipFrom(box) + '. Two warehouses means two deliveries and two freight charges.';
        note.hidden = false;
      }
    }

    function sync() {
      var name = shipFrom(box);
      for (var i = 0; i < rows.length; i++) {
        rows[i].classList.toggle('is-selected', rows[i].getAttribute('data-sj-warehouse') === name);
      }
      if (label && name) label.textContent = name;
      if (onChange) onChange(name);
    }

    // The radio is the source of truth (it is what the form submits). Clicking
    // anywhere on a selectable row checks its radio.
    // A deliberate click is the customer choosing for the whole cart from here on.
    for (var r = 0; r < radios.length; r++) {
      radios[r].addEventListener('change', function () {
        storeWarehouse(this.value);
        if (note) note.hidden = true;
        sync();
      });
    }
    for (var w = 0; w < rows.length; w++) {
      rows[w].addEventListener('click', function (e) {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'LABEL') return;
        var radio = this.querySelector('[data-sj-ship-from-radio]');
        if (radio && !radio.checked) {
          radio.checked = true;
          storeWarehouse(radio.value);
          if (note) note.hidden = true;
          sync();
        }
      });
    }

    applyRemembered();
    sync();
  }

  /* ---------- 5. The cart's warehouse as a cart attribute ----------
     Shopify's pickup at checkout ignores the "Ship from" line property and
     offers every warehouse holding the stock. A Checkout Blocks rule hides
     pickup locations that do not match the cart attribute "Ship from", so
     after every add to cart this sets that attribute to the one warehouse
     every line names, or clears it when the lines disagree. The cart page
     does the same; this covers the customer who goes straight from the
     added-to-cart flyout to checkout. */
  var SHIP_FROM_ATTR = 'Ship from';
  // The companion attribute lists every OTHER warehouse, so a Checkout
  // Blocks rule per warehouse is a single "contains <name>" test. Keep in
  // step with the warehouses that offer pickup.
  var HIDE_ATTR = 'Pickup not at';
  var WAREHOUSES = ['Sydney', 'Melbourne', 'Brisbane', 'Adelaide', 'Perth', 'Townsville'];

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('cart read failed');
      return r.json();
    });
  }

  function unanimousWarehouse(cart) {
    var name = null;
    for (var i = 0; i < cart.items.length; i++) {
      var v = String((cart.items[i].properties || {})[SHIP_FROM_ATTR] || '').trim();
      if (!v) return '';
      if (name === null) name = v;
      else if (name !== v) return '';
    }
    return name || '';
  }

  var syncing = null;
  function syncShipFromAttribute() {
    if (syncing) return syncing;
    syncing = getCart().then(function (cart) {
      var wanted = unanimousWarehouse(cart);
      var hide = '';
      if (wanted) {
        var others = [];
        for (var w = 0; w < WAREHOUSES.length; w++) { if (WAREHOUSES[w] !== wanted) others.push(WAREHOUSES[w]); }
        hide = others.join(', ');
      }
      var current = cart.attributes || {};
      if (wanted === String(current[SHIP_FROM_ATTR] || '') && hide === String(current[HIDE_ATTR] || '')) return cart;
      var attrs = {};
      attrs[SHIP_FROM_ATTR] = wanted;
      attrs[HIDE_ATTR] = hide;
      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).then(function (r) { return r.ok ? r.json() : cart; });
    }).catch(function () { return null; }).then(function (cart) { syncing = null; return cart; });
    return syncing;
  }

  function initShipFromAttribute() {
    syncShipFromAttribute();
    // Empire adds to cart by AJAX from this form; the cart is settled well
    // within a second or two. Sync twice so a slow add is still caught.
    function later() { window.setTimeout(syncShipFromAttribute, 1500); window.setTimeout(syncShipFromAttribute, 4000); }
    document.addEventListener('submit', function (e) {
      if (e.target && e.target.matches && e.target.matches('[data-product-form]')) later();
    }, true);
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('[data-quick-buy]')) later();
    }, true);
    // The flyout's checkout link waits for the attribute, briefly, and never
    // blocks: after 2.5 seconds it proceeds regardless.
    document.addEventListener('click', function (e) {
      var el = e.target && e.target.closest &&
               e.target.closest('button[name="checkout"], input[name="checkout"], a[href^="/checkout"]');
      if (!el || el.getAttribute('data-sj-shipfrom-ok') === '1') return;
      e.preventDefault();
      var done = false;
      function go() {
        if (done) return;
        done = true;
        el.setAttribute('data-sj-shipfrom-ok', '1');
        if (el.tagName === 'A') { window.location.href = el.href; return; }
        var form = el.form || (el.closest && el.closest('form'));
        if (form && form.requestSubmit) form.requestSubmit(el);
        else el.click();
      }
      syncShipFromAttribute().then(go, go);
      window.setTimeout(go, 2500);
    }, true);
  }

  function init() {
    var boxes = document.querySelectorAll('[data-sj-buybox]');
    for (var i = 0; i < boxes.length; i++) {
      initPallet(boxes[i]);
      var rerun = initEstimate(boxes[i]);
      initBuyFrom(boxes[i], rerun);
    }
    if (boxes.length) initFlyoutImageFix(boxes[0]);
    initShipFromAttribute();
  }

  // Empire moves the form between the regular and alt columns at large
  // breakpoints; it moves the same nodes, so listeners survive. Init once.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
